import agreementConfig from '../../../config/agreementConfig.json'

const SECONDS_PER_EPOCH = 30

const SECOND_LEVEL_EVENT_AGREEMENT_PSG_FILE_IDS = Object.freeze(
	agreementConfig.secondLevelEventAgreementPsgFileIds
)

const RESPIRATORY_EVENT_BITS = Object.freeze({
	'Obstructive Hypopnea': 1,
	'Central Hypopnea': 1,
	'Mixed Hypopnea': 1,
	'Hypopnea': 1,
	'Obstructive Apnea': 2,
	'Central Apnea': 4,
	'Mixed Apnea': 8,
	'RERA': 16
})

export function isSecondLevelEventAgreementEnabled(psgFileId) {
	return SECOND_LEVEL_EVENT_AGREEMENT_PSG_FILE_IDS.includes(Number(psgFileId))
}

function fillEventSeconds(target, secondFrom, secondTo, value) {
	const totalSeconds = target.length
	const fromValue = Number(secondFrom)
	const toValue = Number(secondTo)

	if (!Number.isFinite(fromValue) || !Number.isFinite(toValue)) return

	const from = Math.max(0, Math.floor(fromValue))
	const to = Math.min(totalSeconds, Math.ceil(toValue))
	if (from >= to) return

	for (let second = from; second < to; second++) {
		target[second] |= value
	}
}

function buildEventSecondData(events, totalSeconds) {
	const respiratory = new Uint8Array(totalSeconds)
	const arousal = new Uint8Array(totalSeconds)
	const spo2 = new Uint8Array(totalSeconds)

	const eventList = Array.isArray(events) ? events : Object.values(events || {})
	eventList.forEach(event => {
		const respiratoryBit = RESPIRATORY_EVENT_BITS[event.type]
		if (respiratoryBit !== undefined) {
			fillEventSeconds(respiratory, event.secondFrom, event.secondTo, respiratoryBit)
		} else if (event.type === 'ARO SPONT') {
			fillEventSeconds(arousal, event.secondFrom, event.secondTo, 1)
		} else if (event.type === 'SpO2 Desat') {
			fillEventSeconds(spo2, event.secondFrom, event.secondTo, 1)
		}
	})

	return { respiratory, arousal, spo2 }
}

function expandMasterRowToSeconds(row, totalEpoch, totalSeconds) {
	const seconds = new Uint8Array(totalSeconds)
	if (!Array.isArray(row)) return seconds

	// Newer backend data may already be per-second. Historical /msevent data is per 30-second epoch.
	if (row.length >= totalSeconds) {
		for (let second = 0; second < totalSeconds; second++) {
			seconds[second] = Number(row[second]) === 1 ? 1 : 0
		}
		return seconds
	}

	const epochCount = Math.min(totalEpoch, row.length)
	for (let epoch = 0; epoch < epochCount; epoch++) {
		if (Number(row[epoch]) !== 1) continue
		const from = epoch * SECONDS_PER_EPOCH
		const to = Math.min(from + SECONDS_PER_EPOCH, totalSeconds)
		seconds.fill(1, from, to)
	}
	return seconds
}

function buildMasterSecondData(masterSource, totalEpoch, totalSeconds) {
	if (masterSource && Array.isArray(masterSource.events)) {
		return buildEventSecondData(masterSource.events, totalSeconds)
	}

	const rows = Array.isArray(masterSource)
		? masterSource
		: Array.isArray(masterSource && masterSource.rows) ? masterSource.rows : []
	const hypopnea = expandMasterRowToSeconds(rows[0], totalEpoch, totalSeconds)
	const obstructiveApnea = expandMasterRowToSeconds(rows[1], totalEpoch, totalSeconds)
	const centralApnea = expandMasterRowToSeconds(rows[2], totalEpoch, totalSeconds)
	const mixedApnea = expandMasterRowToSeconds(rows[3], totalEpoch, totalSeconds)
	const arousal = expandMasterRowToSeconds(rows[4], totalEpoch, totalSeconds)
	const spo2 = expandMasterRowToSeconds(rows[5], totalEpoch, totalSeconds)
	const respiratory = new Uint8Array(totalSeconds)

	for (let second = 0; second < totalSeconds; second++) {
		// A respiratory second agrees only when the complete set of event types is identical.
		respiratory[second] =
			hypopnea[second] |
			(obstructiveApnea[second] << 1) |
			(centralApnea[second] << 2) |
			(mixedApnea[second] << 3)
	}

	return { respiratory, arousal, spo2 }
}

function calculateAgreement(first, second) {
	let sameSeconds = 0
	for (let secondIndex = 0; secondIndex < first.length; secondIndex++) {
		if (first[secondIndex] === second[secondIndex]) sameSeconds++
	}
	return ((sameSeconds / first.length) * 100).toFixed(1)
}

function normalizeMatrix(matrix) {
	return matrix.map(row => {
		const total = row.reduce((sum, value) => sum + value, 0)
		return row.map(value => {
			if (total === 0) return '-'
			return ((value / total) * 100).toFixed(1)
		})
	})
}

function respiratoryMatrixIndex(value) {
	if (value === 0) return 0
	// The report template has no RERA column, so RERA is grouped with hypopnea.
	if ((value & RESPIRATORY_EVENT_BITS.Hypopnea) !== 0 ||
		(value & RESPIRATORY_EVENT_BITS.RERA) !== 0) return 1
	if ((value & RESPIRATORY_EVENT_BITS['Obstructive Apnea']) !== 0) return 2
	if ((value & RESPIRATORY_EVENT_BITS['Central Apnea']) !== 0) return 3
	return 4
}

export function calculateEventMatrices(events, masterSource, totalEpoch) {
	const totalSeconds = totalEpoch * SECONDS_PER_EPOCH
	if (totalSeconds === 0) return null

	const user = buildEventSecondData(events, totalSeconds)
	const master = buildMasterSecondData(masterSource, totalEpoch, totalSeconds)
	const respiratory = Array(5).fill(null).map(() => Array(5).fill(0))
	const arousal = Array(2).fill(null).map(() => Array(2).fill(0))
	const spo2 = Array(2).fill(null).map(() => Array(2).fill(0))

	for (let second = 0; second < totalSeconds; second++) {
		respiratory[respiratoryMatrixIndex(master.respiratory[second])]
		[respiratoryMatrixIndex(user.respiratory[second])]++
		arousal[master.arousal[second]][user.arousal[second]]++
		spo2[master.spo2[second]][user.spo2[second]]++
	}

	return {
		respiratory: normalizeMatrix(respiratory),
		arousal: normalizeMatrix(arousal),
		spo2: normalizeMatrix(spo2)
	}
}

export function calculateEpochEventMatrices(userEpochs, masterRows, totalEpoch) {
	const rows = Array.isArray(masterRows) ? masterRows : []
	const epochCount = Number(totalEpoch) || 0
	const {
		hypopnea = [],
		obstructiveApnea = [],
		centralApnea = [],
		mixedApnea = [],
		arousal = [],
		spo2 = []
	} = userEpochs || {}
	const userRespiratoryRows = [hypopnea, obstructiveApnea, centralApnea, mixedApnea]
	const masterRespiratoryRows = [rows[0] || [], rows[1] || [], rows[2] || [], rows[3] || []]
	const respiratory = Array(5).fill(null).map(() => Array(5).fill(0))
	const arousalMatrix = Array(2).fill(null).map(() => Array(2).fill(0))
	const spo2Matrix = Array(2).fill(null).map(() => Array(2).fill(0))

	for (let epoch = 0; epoch < epochCount; epoch++) {
		const matchingType = masterRespiratoryRows.findIndex((masterRow, index) =>
			Number(masterRow[epoch]) === 1 && Number(userRespiratoryRows[index][epoch]) === 1
		)
		const masterType = masterRespiratoryRows.findIndex(row => Number(row[epoch]) === 1)
		const userType = userRespiratoryRows.findIndex(row => Number(row[epoch]) === 1)

		if (matchingType >= 0) {
			// Legacy agreement treats the epoch as correct when any respiratory type matches.
			respiratory[matchingType + 1][matchingType + 1]++
		} else {
			respiratory[masterType + 1][userType + 1]++
		}

		const masterArousal = Number(rows[4] && rows[4][epoch]) === 1 ? 1 : 0
		const userArousal = Number(arousal[epoch]) === 1 ? 1 : 0
		const masterSpo2 = Number(rows[5] && rows[5][epoch]) === 1 ? 1 : 0
		const userSpo2 = Number(spo2[epoch]) === 1 ? 1 : 0
		arousalMatrix[masterArousal][userArousal]++
		spo2Matrix[masterSpo2][userSpo2]++
	}

	return {
		respiratory: normalizeMatrix(respiratory),
		arousal: normalizeMatrix(arousalMatrix),
		spo2: normalizeMatrix(spo2Matrix)
	}
}

export function calculateEventAgreement(events, masterSource, totalEpoch) {
	const totalSeconds = totalEpoch * SECONDS_PER_EPOCH
	if (totalSeconds === 0) {
		return {
			respiratory: null,
			arousal: null,
			spo2: null
		}
	}

	const user = buildEventSecondData(events, totalSeconds)
	const master = buildMasterSecondData(masterSource, totalEpoch, totalSeconds)

	return {
		respiratory: calculateAgreement(user.respiratory, master.respiratory),
		arousal: calculateAgreement(user.arousal, master.arousal),
		spo2: calculateAgreement(user.spo2, master.spo2)
	}
}

export function calculateEpochEventAgreement(userEpochs, masterRows, totalEpoch) {
	const rows = Array.isArray(masterRows) ? masterRows : []
	const epochCount = Number(totalEpoch) || 0
	const {
		hypopnea = [],
		obstructiveApnea = [],
		centralApnea = [],
		mixedApnea = [],
		arousal = [],
		spo2 = []
	} = userEpochs || {}
	let sameRespiratory = 0
	let sameArousal = 0
	let sameSpo2 = 0

	for (let epoch = 0; epoch < epochCount; epoch++) {
		const bothHaveNoRespiratoryEvent =
			Number(obstructiveApnea[epoch]) === 0 &&
			Number(centralApnea[epoch]) === 0 &&
			Number(mixedApnea[epoch]) === 0 &&
			Number(hypopnea[epoch]) === 0 &&
			Number(rows[1] && rows[1][epoch]) === 0 &&
			Number(rows[2] && rows[2][epoch]) === 0 &&
			Number(rows[3] && rows[3][epoch]) === 0 &&
			Number(rows[0] && rows[0][epoch]) === 0

		const hasMatchingRespiratoryType =
			(Number(obstructiveApnea[epoch]) === 1 && Number(rows[1] && rows[1][epoch]) === 1) ||
			(Number(centralApnea[epoch]) === 1 && Number(rows[2] && rows[2][epoch]) === 1) ||
			(Number(mixedApnea[epoch]) === 1 && Number(rows[3] && rows[3][epoch]) === 1) ||
			(Number(hypopnea[epoch]) === 1 && Number(rows[0] && rows[0][epoch]) === 1)

		if (bothHaveNoRespiratoryEvent || hasMatchingRespiratoryType) sameRespiratory++
		if (Number(rows[4] && rows[4][epoch]) === Number(arousal[epoch])) sameArousal++
		if (Number(rows[5] && rows[5][epoch]) === Number(spo2[epoch])) sameSpo2++
	}

	const percentage = same => epochCount === 0 ? null : ((same / epochCount) * 100).toFixed(1)
	return {
		respiratory: percentage(sameRespiratory),
		arousal: percentage(sameArousal),
		spo2: percentage(sameSpo2)
	}
}

function normalizeAgreementValue(value) {
	if (value === null || value === undefined || value === 'null') return null
	const numericValue = Number(value)
	return Number.isFinite(numericValue) ? numericValue.toFixed(1) : String(value)
}

export function hasAgreementChanged(savedAgreement, nextAgreement) {
	let savedValues = savedAgreement
	if (Array.isArray(savedAgreement) && savedAgreement.length > 0 &&
		savedAgreement[0] !== null && typeof savedAgreement[0] === 'object') {
		// Multiple rows for the same agreement key must be collapsed by /set_agreement.
		if (savedAgreement.length > 1) return true
		const savedRow = savedAgreement[0]
		savedValues = [
			savedRow.stage,
			savedRow.respiratory,
			savedRow.arousal,
			savedRow.spo2,
			savedRow.limb,
			savedRow.rwa
		]
	}

	if (!Array.isArray(savedValues) || savedValues.length !== nextAgreement.length) return true
	return savedValues.some((value, index) =>
		normalizeAgreementValue(value) !== normalizeAgreementValue(nextAgreement[index])
	)
}
